import { equals, isEmpty, keys, map, omit, pick, remove, update, whereEq } from 'ramda';
import type { Location, Path } from 'slate';
import { Editor, Node, Point, Range, Text } from 'slate';

import type { AlineText, FormatKeys, TextFormatEdit, TextPropsWithoutComments } from '../internal';
import {
  AlineTransforms,
  isTextFormatEdit,
  Queries,
  textPropKeysWithoutComments,
} from '../internal';

export type OmitOriginFromEditVal = {
  [K in keyof TextFormatEdit['value']]: Omit<TextFormatEdit['value'][K], 'origin'>;
};

const formatQueries = {
  /**
   * Returns the formats that are consistent across the selection.  Includes suggested edits as edits drive format rendering as well.
   */
  consistentFormats(
    editor: Editor,
    options: { at?: Location } = {}
  ): Partial<TextPropsWithoutComments> | null {
    const formats = Queries.formats(editor, options);

    if (Array.isArray(formats)) {
      let consistentFormats: Partial<TextPropsWithoutComments> | undefined = formats.at(0);
      if (!consistentFormats) return null;

      /**
       * Remove any formats in consistent formats with the same keys as suggestedEdits since the keys in suggested edits are what is rendered.
       * In other words, we don't want to loop over keys in consistent formats that are also in suggested edits since suggested edits are what is rendered.
       */
      const editKeys = consistentFormats.suggestedEdits
        ?.filter(isTextFormatEdit)
        .flatMap(edit => keys(edit.value));

      if (editKeys && !isEmpty(editKeys)) consistentFormats = omit(editKeys, consistentFormats);

      for (const formatKey of keys(consistentFormats)) {
        for (let i = 0; i < formats.length; i++) {
          const nextFormatSet: TextPropsWithoutComments | undefined = formats[i + 1];

          if (formatKey !== 'suggestedEdits') {
            if (!nextFormatSet) continue;

            /**
             * Omit the 'origin' prop from all formats as we only care about comparing values when determining consistent formats.
             * Ramda's omit is safe here since, if origin is missing in the object, it will just return the object without any keys omitted.
             */
            const referenceFormat = omit(['origin'], consistentFormats[formatKey]);
            const nextFormat = omit(['origin'], nextFormatSet[formatKey]);
            const nextFormatSetMatchingEdit = (nextFormatSet.suggestedEdits || [])
              .filter(isTextFormatEdit)
              .find(edit => formatKey in edit.value);

            if (nextFormatSetMatchingEdit) {
              const valueWithoutOrigin = omit(
                ['origin'],
                nextFormatSetMatchingEdit.value[formatKey]
              );

              if (!equals(referenceFormat, valueWithoutOrigin)) {
                consistentFormats = omit([formatKey], consistentFormats);
                break;
              }

              // Only continue since the next text node proper may contain an equal format.
              continue;
            }

            if (!equals(referenceFormat, nextFormat)) {
              consistentFormats = omit([formatKey], consistentFormats);
              // We know that the format at this key is not on the next text node proper or in its suggested edit so we can break.
              break;
            }
          } else {
            if (!nextFormatSet) continue;

            const suggestedTextEdits = consistentFormats[formatKey];
            const nextTextEdits = nextFormatSet[formatKey];

            if (!suggestedTextEdits || isEmpty(suggestedTextEdits)) {
              consistentFormats = omit([formatKey], consistentFormats);
              break;
            }

            const alreadyAddressedKeys = new Set<FormatKeys>();

            for (const [index, consistentEdit] of suggestedTextEdits.entries()) {
              if (!isTextFormatEdit(consistentEdit)) continue;

              const editValWithoutOrigin = map<TextFormatEdit['value'], OmitOriginFromEditVal>(
                edit => omit(['origin'], edit),
                consistentEdit.value
              );

              // Set up an array of keys to track what formats within this edit have consistent formatting, if any.
              const consistentKeys: FormatKeys[] = [];

              for (const key of keys(editValWithoutOrigin)) {
                /**
                 * This is tricky, but if we've already encountered an edit with, say, a 'bold' key, that is the edit that would be rendered.
                 * If there is another edit in the consistentEdits array with a 'bold' key too, we want to ignore that edit.  For instance, it could be a 'bold' edit
                 * made by another user at an earlier point in time that is not going to be rendered and, therefore, can't contribute to consistent formats.
                 */
                if (alreadyAddressedKeys.has(key)) continue;

                alreadyAddressedKeys.add(key);

                const editValAtKey = editValWithoutOrigin[key];

                const nextHasEqualEdit =
                  nextTextEdits?.filter(isTextFormatEdit).some(edit => {
                    const nextEditValAtKey = omit(['origin'], edit.value[key]);
                    return equals(editValAtKey, nextEditValAtKey);
                  }) ?? false;

                // If nextFormats has a matching suggested edit for this key, the format at this key is consistent.
                if (nextHasEqualEdit) {
                  consistentKeys.push(key);
                  continue;
                }

                /**
                 * The nextFormats may not have a matching edit, but it can have a format(s) on the text node proper that matches the edit at this key.
                 */
                const nextValAtKey = nextFormatSet[key];

                if (!nextValAtKey) continue;

                const nextValWithoutOrigin = omit(['origin'], nextValAtKey);

                if (!equals(editValAtKey, nextValWithoutOrigin)) continue;

                consistentKeys.push(key);
              }

              if (isEmpty(consistentKeys)) {
                consistentFormats[formatKey] = remove(index, 1, suggestedTextEdits);
                break;
              }

              const updatedEdit: TextFormatEdit = {
                ...consistentEdit,
                value: pick(consistentKeys, consistentEdit.value),
              };

              consistentFormats[formatKey] = update(index, updatedEdit, suggestedTextEdits);
            }
          }
        }
      }

      return consistentFormats;
    }

    return formats;
  },

  /**
   * Returns the formats present at a Location.  Defaults to the current selection.
   * Includes suggested edits as edits can drive format rendering as well.
   */
  formats: (
    editor: Editor,
    options: {
      at?: Location;
      ignoreTextNodesAroundTab?: boolean;
      voids?: boolean;
    } = {}
  ): TextPropsWithoutComments[] | Partial<TextPropsWithoutComments> | null => {
    let { at = editor.selection } = options;
    const { ignoreTextNodesAroundTab = false, voids = true } = options;

    if (!at) return null;

    // If it's the selection and it's collapsed, return the cursor formats.
    if (at === editor.selection && Range.isCollapsed(at)) {
      const { anchor } = at;

      return Queries.formatsAtCursor(editor, { at: anchor });
    }

    if (Point.isPoint(at)) {
      const { path } = at;
      const leaf = Node.leaf(editor, path);

      return pickTextPropsWithoutComments(leaf);
    }

    if (Queries.getGridSelectionInfoForLocation(editor, { at }))
      return [...Queries.nodes<AlineText>(editor, { at, match: Text.isText, voids })].map(
        ([textNode]) => pickTextPropsWithoutComments(textNode)
      );

    if (Range.isRange(at) && ignoreTextNodesAroundTab) {
      const unhungRange = AlineTransforms.unhangRangeWithTab(editor, at);

      if (unhungRange) at = unhungRange;
    }

    const textNodeEntries = [
      ...Editor.nodes<AlineText>(editor, {
        at,
        match: Text.isText,
        voids,
      }),
    ].filter(([, path]) => !Range.isRange(at) || getRangeIncludesTextNodeContent(editor, at, path));

    return textNodeEntries.map(([textNode]) => pickTextPropsWithoutComments(textNode));
  },

  /**
   * Returns the text formats present at the cursor.  Defaults to the current selection.
   */
  formatsAtCursor: (
    editor: Editor,
    options: { at?: Point; voids?: boolean } = {}
  ): TextPropsWithoutComments | null => {
    const { marks, selection } = editor;
    const { at = selection?.anchor } = options;

    if (!at) return null;
    if (marks) return pick(textPropKeysWithoutComments, marks);

    const { path } = at;
    const node = Node.leaf(editor, path);

    return pick(textPropKeysWithoutComments, node);
  },

  /**
   * Returns the formats present on the first text node at a Location.
   * Used for inline styling when a user had modified the text of the inline.
   */
  getFirstTextFormats: (
    editor: Editor,
    options: { at?: Location; voids?: boolean } = {}
  ): TextPropsWithoutComments | null => {
    const { at = editor.selection, voids = false } = options;

    if (!at) {
      console.warn(`The at parameter is ${at}.  Pass a valid location.`);
      return null;
    }

    const textEntries = Editor.nodes<AlineText>(editor, {
      at,
      match: Text.isText,
      voids,
    });

    const firstEntry = textEntries.next().value;

    if (!firstEntry) return null;

    const [firstText] = firstEntry;

    return pickTextPropsWithoutComments(firstText);
  },

  /**
   * Returns whether the designated formatting is active based on the user's
   * current selection.
   *
   * The function is meant only for use with format keys that have an 'active'
   * property (e.g., bold, italic, etc.).
   *
   * The `listMarker` option indicates whether we should query list markers, if
   * `at` is a list marker selection.
   */
  isTextFormatActive: (
    editor: Editor,
    formatKey: FormatKeys,
    options: {
      at?: Location;
      listMarker?: boolean;
      voids?: boolean;
    } = {}
  ): boolean => {
    const { at = editor.selection, listMarker = false, voids = true } = options;

    if (!at) {
      console.warn(`The at parameter is ${at}.  Pass a non-null Range.`);
      return false;
    }

    if (at === editor.selection && editor.selection.listMarker && listMarker) {
      const listMarkerFormats = Queries.activeListMarkerAtLocation(editor, { at });
      if (!listMarkerFormats) return false;

      const format = listMarkerFormats[formatKey];
      if (!format) return false;

      return whereEq({ active: true }, format);
    }

    let formatActiveOnAllTextNodes = true;

    const formats = Queries.formats(editor, {
      at,
      ignoreTextNodesAroundTab: true,
      voids,
    });

    if (!formats) return false;

    function formatIsActive(
      formats: TextPropsWithoutComments | Partial<TextPropsWithoutComments>
    ): boolean {
      const renderedFormat = Queries.renderedFormat(formatKey, formats);

      if (!renderedFormat) return false;

      return (
        whereEq({ active: true }, renderedFormat) ||
        whereEq({ align: 'superscript' }, renderedFormat) ||
        whereEq({ align: 'subscript' }, renderedFormat)
      );
    }

    if (Array.isArray(formats)) {
      for (const format of formats) {
        const formatActive = formatIsActive(format);

        if (!formatActive) {
          formatActiveOnAllTextNodes = false;
          break;
        }
      }
    } else {
      formatActiveOnAllTextNodes = formatIsActive(formats);
    }

    return formatActiveOnAllTextNodes;
  },

  renderedFormat(
    formatKey: FormatKeys,
    formats: TextPropsWithoutComments | Partial<TextPropsWithoutComments>
  ) {
    const matchingFormatEdit = formats.suggestedEdits
      ?.filter(isTextFormatEdit)
      .find(textFormatEdit => formatKey in textFormatEdit.value);

    const renderedFormat = matchingFormatEdit
      ? matchingFormatEdit.value[formatKey]
      : formats[formatKey];

    return renderedFormat;
  },
};

export default formatQueries;

/**
 * Pick a text node's format props. Confines the one assertion `pick` needs here.
 * TODO: Remove the type assertion when the `[key: string]: unknown` index signature is removed from
 * Slate's node types and `pick` can infer the result.
 */
function pickTextPropsWithoutComments(textNode: AlineText): TextPropsWithoutComments {
  return pick(textPropKeysWithoutComments, textNode) as TextPropsWithoutComments;
}

function getRangeIncludesTextNodeContent(editor: Editor, range: Range, path: Path) {
  const rangeEnd = Range.end(range);
  const rangeStart = Range.start(range);
  const textNodeEnd = Editor.end(editor, path);
  const textNodeStart = Editor.start(editor, path);
  const isRangeStartAtTextNodeEnd = Point.equals(rangeStart, textNodeEnd);
  const isRangeEndAtTextNodeStart = Point.equals(rangeEnd, textNodeStart);

  return !isRangeStartAtTextNodeEnd && !isRangeEndAtTextNodeStart;
}
